/**
 * @file GridBlock.tsx
 * @brief Browserbase-style section grid — hairline cells, no card chrome.
 *
 * @details
 * `GridBlock` opens the left spine only. The shell topbar already draws
 * `border-b`, so a `border-t` here stacked flush under the header read as
 * a double line (and showed clearly on overscroll). Each `GridCell` /
 * `GridBand` closes with right/bottom borders.
 * Use `padded={false}` for flush data tables (avoids left/right inset from
 * default cell padding). Page roots use `gridPageBleedClass` for vertical
 * spacing; the shell main rail is already unpadded so negative `mx` bleed
 * is unnecessary.
 *
 * @author DES
 * @date 29-07-2026
 * @updated 30-07-2026 — drop border-t (topbar supplies the top hairline)
 */

import type { HTMLAttributes, PropsWithChildren } from "react";
import { cn } from "@/shared/theme/cn";

type DivProps = HTMLAttributes<HTMLDivElement>;

/** Outer section frame — left spine only (top line comes from the topbar).
 *  No fill: the page's own background shows through (DES 30-09-2026, "baki
 *  pages main say bhi bg kay saath wohi karo jo session walay page per kia
 *  hai"). */
export function GridBlock({ className, children, ...props }: DivProps) {
  return (
    <section
      className={cn("border-l border-border/60", className)}
      {...props}
    >
      {children}
    </section>
  );
}

/** Full-width band inside a GridBlock (headers, toolbars, banners). */
export function GridBand({ className, children, ...props }: DivProps) {
  return (
    <div
      className={cn(
        "border-b border-r border-border/60 px-4 py-4 md:px-5",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

/** CSS grid wrapper; place `GridCell` children inside. */
export function GridCells({ className, children, ...props }: DivProps) {
  return (
    <div className={cn("grid", className)} {...props}>
      {children}
    </div>
  );
}

/** One cell — draws the closing right + bottom hairlines. */
export function GridCell({
  className,
  children,
  /** When false, no inset padding (flush data grids). Default true. */
  padded = true,
  /**
   * Form field cells: a label and one input need far less air than a chart or a
   * stat. Same side padding, tighter top/bottom, so a form is not a tower of
   * 106px rows (DES, 30-09-2026). Data and chart cells keep the default.
   */
  dense = false,
  ...props
}: PropsWithChildren<DivProps & { padded?: boolean; dense?: boolean }>) {
  return (
    <div
      className={cn(
        "min-w-0 border-b border-r border-border/60",
        padded && (dense ? "px-4 py-3 md:px-5" : "p-4 md:p-5"),
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}
